import { useMediaQuery } from '@mui/system';
import React from 'react';
import { Box } from 'src/components-v2/Layout';
import { isAutomatedBrowser } from 'src/lib/browser-utils';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import theme from 'src/components-v2/theme';

export interface MomentProps {
  delay?: number;
  sx?: SxProps<ITheme>;
}

const FloatingMoment: React.FC<React.PropsWithChildren<MomentProps>> = ({
  children,
  delay = 250,
  sx,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  // Start settled under automated capture so snapshots never catch the
  // entrance transition mid-flight.
  const settleImmediately = isAutomatedBrowser();
  const [shouldShow, setShouldShow] = React.useState(settleImmediately);
  const aboveMd = useMediaQuery(theme.breakpoints.up('md'));

  const onObserve = (entries) => {
    // allow count if visible
    entries.forEach((entry) => {
      if (entry.isIntersecting && !shouldShow) {
        setTimeout(() => {
          setShouldShow(true);
        }, delay);
      }
    });
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(onObserve, {
      rootMargin: `0px 0px 0px 0px`,
      threshold: 0.2,
    });
    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [onObserve, shouldShow]);

  return (
    <Box
      ref={ref}
      sx={{
        position: 'absolute',
        transform:
          shouldShow || !aboveMd
            ? 'translateY(-25px) scale(1)'
            : 'translateY(0) scale(0.9)',
        opacity: shouldShow || !aboveMd ? 1 : 0,
        transition:
          aboveMd && !settleImmediately && 'transform 1.5s, opacity 1.5s',
        display: {
          xs: 'none',
          lg: 'block',
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default FloatingMoment;
